import { FC, useEffect, useState } from "react";
import { DateTime } from "luxon";
import { useFormik } from "formik";
import {
  Button,
  DatePicker,
  Divider,
  InputField,
  Slider,
} from "../../../../components";
import { useLoanApplication, useLoanCalculator } from "../../../../contexts";
import {
  PayablesFinanceLoanCalculatorFields,
  getPayablesFinanceLoanValidationSchema,
} from "../../service";
import {
  LoanApplicationStage,
  LoanDurationType,
  LoanProductType,
  MediaPurpose,
  MediaType,
} from "../../../../hooks/api/types";
import { roundTo, toTitleCase } from "../../../../utils";
import { useStepper } from "../../../LoanApplicationSteps/Stepper";
import { ArrowLeftIcon } from "../../../../assets/icons";
import { useLoanMetrics, useMedia } from "../../../../hooks";
import { useDpConfig } from "../../../../contexts/dpConfig";
import TermsAndConditionsSection from "../../TermsAndConditionsSection";
import { useLoanProductConfigFlags } from "../../../../hooks/useLoanProductConfigFlags";

import "../../ReactDatePickerOverride.css";
import { Tags as LoanCalculatorTags } from "../../../../components/Tags";
import { MessageBar as LoanCalculatorMessage } from "../../../../components/MessageBar";

const DUMMY_INVOICE_NAME = "dummy-invoice.pdf";

const PAGE_COPY: {
  [key: string]: {
    default: (...args: any[]) => string;
    repayableByDP: (...args: any[]) => string;
  };
} = {
  TITLE: {
    default: () => "Total repayment amount",
    repayableByDP: () => "Total payment amount",
  },
  SUBTITLE: {
    default: (interestRate: number) =>
      `Pay as low as ${interestRate}% monthly interest*.`,
    repayableByDP: (interestRate: number) =>
      `Pay from ${interestRate}% monthly fees*.`,
  },
  LOAN_AMOUNT: {
    default: () => "Loan amount",
    repayableByDP: () => "Invoice amount",
  },
  LOAN_DURATION: {
    default: () => "Loan duration",
    repayableByDP: () => "Invoice due date",
  },
};

function getCopyText(key: string, repayableByDPFlag?: boolean) {
  return PAGE_COPY[key][repayableByDPFlag ? "repayableByDP" : "default"];
}

type Props = {
  editMode?: boolean;
  tags?: string[];
  message?: string;
  handleSubmit?: () => void;
  handleCancel?: () => void;
};

const PayablesFinanceLoanCalculator: FC<Props> = ({
  editMode = false,
  tags = [],
  message = "",
  handleSubmit: handleSubmitFromParent,
  handleCancel: handleCancelFromParent,
}) => {
  const [loading, setLoading] = useState(false);
  const [isPartnerTermsAccepted, setIsPartnerTermsAccepted] =
    useState<boolean>(false);
  const {
    state: { payableFinancingLoan, constraints },
    actions: { setPayableFinancingLoan },
  } = useLoanCalculator();
  const {
    state: { dpTermsUrl, uiLocalizationData },
  } = useDpConfig();
  const { nextStep } = useStepper();
  const {
    state: {
      settings: { moveLoanCalculatorAtStart },
    },
  } = useDpConfig();
  const { disableInvoiceUpload } = useLoanProductConfigFlags(
    LoanProductType.PAYABLE_FINANCING
  );
  const {
    actions: { update, refetch },
    state: { id, invoiceFiles, sourceId, stage },
  } = useLoanApplication();
  const { upload } = useMedia();

  const {
    payableFinancingProduct: {
      flags: { repayableByDP },
    },
    totalPayableFinancingPaymentForSME,
  } = useLoanMetrics();

  const [currentInvoice] = payableFinancingLoan.invoices;

  const [invoiceDueDate, setInvoiceDueDate] = useState<Date | null>(
    repayableByDP
      ? currentInvoice?.invoiceDueDate ??
          DateTime.fromJSDate(new Date())
            .plus({
              days: constraints.payableFinancingLoan?.duration?.initial,
            })
            .toJSDate()
      : null
  );

  const {
    values,
    errors,
    setFieldValue,
    handleSubmit,
    isSubmitting,
    setSubmitting,
  } = useFormik<PayablesFinanceLoanCalculatorFields>({
    enableReinitialize: true,
    initialValues: {
      loanAmount:
        currentInvoice && !isNaN(currentInvoice.amount)
          ? currentInvoice.amount
          : constraints.payableFinancingLoan?.amount?.initial,
      duration:
        currentInvoice && !isNaN(currentInvoice.duration)
          ? currentInvoice.duration
          : constraints.payableFinancingLoan?.duration?.initial,
    },
    validationSchema: getPayablesFinanceLoanValidationSchema({
      loanAmount: {
        min: constraints.payableFinancingLoan?.amount?.min,
        max: constraints.payableFinancingLoan?.amount?.max,
        unit: constraints.payableFinancingLoan?.amount?.unit ?? "AED",
      },
      duration: {
        min: constraints.payableFinancingLoan?.duration?.min,
        max: constraints.payableFinancingLoan?.duration?.max,
      },
    }),
    onSubmit: async () => {
      setSubmitting(true);
      if (repayableByDP) {
        currentInvoice.invoiceDueDate = invoiceDueDate!;
      }
      setPayableFinancingLoan({
        invoices: [currentInvoice],
      });
      // if invoice upload is disabled, we need to update the loan application with a dummy invoice file
      if (disableInvoiceUpload) {
        await update(
          {
            invoiceFiles: [new File([], DUMMY_INVOICE_NAME)],
          },
          {
            //if its legacy flow, we update local state else we update the loan application on BE as well
            local: moveLoanCalculatorAtStart,
          }
        );
        // if LOC flow is disabled, we go to next step
        if (moveLoanCalculatorAtStart) {
          setSubmitting(false);
          nextStep();
        }
      } else {
        await handlePayableFinancingLoanUpdate(id, [
          new File([], DUMMY_INVOICE_NAME),
        ]);
        let nextStage = LoanApplicationStage.OWNER_INFORMATION;
        if (sourceId) {
          nextStage = LoanApplicationStage.DOCUMENTS_UPLOAD;
        }

        await update({
          stage: nextStage,
        });
        setSubmitting(false);
        nextStep();
      }
    },
  });

  useEffect(() => {
    // FYI: this code will never run as Payable finance product is not enabled for any DP with legacy flow
    //if legacy flow is enabled, and invoice file upload is disabled, we need to update the loan application with a dummy invoice file
    if (!moveLoanCalculatorAtStart) {
      if (invoiceFiles.length > 0) {
        if (disableInvoiceUpload) {
          handlePayableFinancingLoanUpdate(id, [
            new File([], DUMMY_INVOICE_NAME),
          ]).then(() => {
            update({
              stage: sourceId
                ? LoanApplicationStage.VENDOR_INFORMATION
                : LoanApplicationStage.OWNER_INFORMATION,
            });
          });
        } else {
          //else we go to invoice upload page
          stage !== LoanApplicationStage.REVIEW && nextStep();
        }
      }
    }
  }, [invoiceFiles]);

  useEffect(() => {
    if (!payableFinancingLoan.invoices?.length) {
      setPayableFinancingLoan({
        invoices: currentInvoice
          ? [currentInvoice]
          : [
              {
                amount: constraints.payableFinancingLoan?.amount?.initial,
                duration: constraints.payableFinancingLoan?.duration?.initial,
                discountRate: 100,
                interestRate: constraints.payableFinancingLoan.interestRate,
                invoiceId: "",
                durationType: constraints.payableFinancingLoan.duration
                  .unit as LoanDurationType,
                invoiceDueDate: new Date(),
                invoiceIssuedDate: new Date(),
                invoiceNo: "",
                vendorName: "",
              },
            ],
      });
    }
  }, [values, payableFinancingLoan.invoices?.length]);

  useEffect(() => {
    // Capture the state *as it is* when the component mounts in edit mode
    // Ensure a deep copy if invoices array contains objects that might be mutated elsewhere
    const initialState =
      editMode && payableFinancingLoan.invoices?.length
        ? JSON.parse(JSON.stringify(payableFinancingLoan)) // Use deep copy
        : null;
    return () => {
      if (editMode && initialState) {
        setPayableFinancingLoan(initialState);
      }
    };
  }, []);

  async function handlePayableFinancingLoanUpdate(
    loanApplicationId: string,
    invoiceFiles: File[]
  ) {
    setLoading(true);
    const invoicePromises = invoiceFiles.map((file) =>
      upload({
        file,
        type: MediaType.DOCUMENT,
        purpose: MediaPurpose.INVOICE,
        loanApplicationId: loanApplicationId,
      })
    );
    const invoiceIds = await Promise.all(invoicePromises);
    if (invoiceIds.length !== 0) {
      const invoices = payableFinancingLoan.invoices.map((invoice, index) => ({
        ...invoice,
        invoiceId: invoiceIds[index],
        discountRate: invoice.discountRate,
        ...(invoice.invoiceDueDate && {
          invoiceDueDate: invoice.invoiceDueDate,
        }),
      }));
      await update({
        id: loanApplicationId,
        loanType: LoanProductType.PAYABLE_FINANCING,
        invoices,
      });
      await refetch();
    }
    setLoading(false);
  }

  const handleInputChange = (name: string, value: string) => {
    const num = Number(value.replaceAll(",", ""));
    if (isNaN(num)) {
      return null;
    }

    setFieldValue(name, num, true);

    const validation: any = {
      loanAmount: constraints.payableFinancingLoan.amount,
      duration: constraints.payableFinancingLoan.duration,
      discountRate: constraints.payableFinancingLoan.discount,
    };

    if (num > validation[name].max || num < validation[name].min) {
      return null;
    }

    return num;
  };

  const handleLoanAmountInputChange = (value: string) => {
    const val = handleInputChange("loanAmount", value);
    if (val !== null) {
      currentInvoice.amount = val;
      currentInvoice.interestRate =
        constraints.payableFinancingLoan.interestRate;
      setPayableFinancingLoan({
        invoices: [currentInvoice],
      });
    }
  };

  const handleLoanAmountSliderChange = (value: number) => {
    setFieldValue("loanAmount", value);
    currentInvoice.amount = value;
    currentInvoice.interestRate = constraints.payableFinancingLoan.interestRate;
    setPayableFinancingLoan({
      invoices: [currentInvoice],
    });
  };

  const handleDurationInputChange = (value: string, date?: Date) => {
    const val = handleInputChange("duration", value);
    if (val !== null) {
      currentInvoice.duration = val;
      currentInvoice.interestRate =
        constraints.payableFinancingLoan.interestRate;
      if (date) {
        currentInvoice.invoiceDueDate = date;
      }
      setPayableFinancingLoan({
        invoices: [currentInvoice],
      });
    }
  };

  const handleDurationSliderChange = (value: number) => {
    setFieldValue("duration", value);
    currentInvoice.duration = value;
    currentInvoice.interestRate = constraints.payableFinancingLoan.interestRate;
    setPayableFinancingLoan({
      invoices: [currentInvoice],
    });
  };

  const handleTermsChange = (checked: boolean) => {
    setIsPartnerTermsAccepted(checked);
  };

  return (
    <div className="cx-w-full cx-flex cx-flex-col cx-p-10 cx-gap-10 cx-items-center cx-rounded-modal cx-border-0.5 cx-border-[#DDD] cx-shadow-modal cx-background-calculator-gradient">
      <div className="cx-flex cx-flex-col cx-items-center cx-w-full">
        <div className="cx-font-medium">
          {getCopyText("TITLE", repayableByDP)()}
        </div>
        <div className="cx-text-3xl cx-font-bold cx-inline-flex cx-items-center cx-gap-1">
          <div className="cx-font-aed-font"> D </div>
          <span>
            {totalPayableFinancingPaymentForSME &&
              roundTo(totalPayableFinancingPaymentForSME, 2).toLocaleString()}
          </span>
        </div>
        <div className="cx-text-sm cx-text-text-secondary">
          {getCopyText(
            "SUBTITLE",
            repayableByDP
          )(roundTo((currentInvoice?.interestRate / 12) * 100, 2))}
        </div>
        <LoanCalculatorTags tags={tags} />
      </div>
      <Divider />
      <div className="cx-flex cx-flex-col md:cx-flex-row cx-w-full cx-gap-6">
        <div className="cx-flex cx-flex-col cx-gap-4 cx-w-full">
          <div className="cx-font-semibold">
            {getCopyText("LOAN_AMOUNT", repayableByDP)()}
          </div>
          <div>
            <InputField
              inputProps={{
                name: "loanAmount",
                value: values.loanAmount?.toLocaleString(),
                onChange: (e) => handleLoanAmountInputChange(e.target.value),
              }}
              inputClassName="cx-text-right !cx-p-2 cx-font-bold"
              prepend={constraints.payableFinancingLoan?.amount?.unit}
              error={errors.loanAmount}
              hideErrorMessage
            />
          </div>
          {!repayableByDP && (
            <div className="">
              <Slider
                initial={values.loanAmount}
                min={constraints.payableFinancingLoan?.amount?.min}
                max={constraints.payableFinancingLoan?.amount?.max}
                unit={constraints.payableFinancingLoan?.amount?.unit}
                value={values.loanAmount}
                label=""
                onChange={(value) => handleLoanAmountSliderChange(value)}
                renderThumb={false}
                interval={constraints.payableFinancingLoan?.amount?.interval}
              />
            </div>
          )}
          {errors.loanAmount && (
            <div className="cx-text-xs cx-py-1 cx-text-text-error">
              {errors.loanAmount}
            </div>
          )}
        </div>
        <div className="cx-flex cx-flex-col cx-gap-4 cx-w-full">
          <div className="cx-font-semibold">
            {getCopyText("LOAN_DURATION", repayableByDP)()}
          </div>
          <div>
            {repayableByDP ? (
              <DatePicker
                selectedDate={invoiceDueDate!}
                min={DateTime.fromJSDate(new Date())
                  .plus({
                    days: constraints.payableFinancingLoan?.duration?.min,
                  })
                  .toJSDate()}
                max={DateTime.fromJSDate(new Date())
                  .plus({
                    days: constraints.payableFinancingLoan?.duration?.max,
                  })
                  .toJSDate()}
                onChange={(date) => {
                  const now = DateTime.fromJSDate(new Date());
                  const invoiceDate = DateTime.fromJSDate(date);
                  const duration = invoiceDate.diff(now, "days").as("days");
                  setInvoiceDueDate(date);
                  handleDurationInputChange(
                    Math.ceil(duration).toString(),
                    date
                  );
                }}
                className="!cx-py-2 cx-font-bold"
              />
            ) : (
              <InputField
                inputProps={{
                  name: "duration",
                  value: values.duration,
                  onChange: (e) => handleDurationInputChange(e.target.value),
                }}
                inputClassName="cx-text-right !cx-p-2 cx-font-bold"
                prepend={toTitleCase(
                  constraints.payableFinancingLoan?.duration?.unit
                )}
              />
            )}
          </div>
          {!repayableByDP && (
            <div className="">
              <Slider
                initial={values.duration}
                min={constraints.payableFinancingLoan?.duration?.min}
                max={constraints.payableFinancingLoan?.duration?.max}
                unit={constraints.payableFinancingLoan?.duration?.unit}
                label=""
                onChange={(value) => handleDurationSliderChange(value)}
                renderThumb={false}
              />
            </div>
          )}
        </div>
      </div>
      {/* <Hint
        text={`${getCopyText(
          "LOAN_AMOUNT",
          repayableByDP
        )()} should be exclusive of VAT.`}
        rounded
        icon
        className="cx-w-full -cx-mt-6"
      /> */}
      <div className="md:cx-w-[400px] cx-w-[263px]">
        {editMode ? (
          <div className="cx-w-full cx-flex cx-flex-row cx-gap-x-4">
            <div className="cx-w-1/4">
              <Button
                label={<ArrowLeftIcon />}
                type="button"
                onClick={handleCancelFromParent}
                fullWidth
                outlined
              />
            </div>
            <div className="cx-w-3/4">
              <Button
                type="button"
                label="Confirm"
                fullWidth
                onClick={() => {
                  setSubmitting(true);
                  handleSubmitFromParent?.();
                }}
                disabled={isSubmitting}
              />
            </div>
          </div>
        ) : (
          <Button
            label="Continue"
            arrow="right"
            loader={loading ? "left" : undefined}
            fullWidth
            onClick={handleSubmit}
            disabled={
              (!isPartnerTermsAccepted && !!dpTermsUrl) ||
              loading ||
              Object.keys(errors).length > 0 ||
              isSubmitting
            }
          />
        )}
      </div>
      <div className="cx-block">
        {uiLocalizationData?.loanCalculatorScreen?.disclaimer && (
          <p className="cx-font-normal cx-text-sm cx-text-text-secondary cx-text-center cx-mt-1">
            {uiLocalizationData?.loanCalculatorScreen?.disclaimer}
          </p>
        )}
      </div>
      <div className="cx-block">
        <TermsAndConditionsSection
          onTermsChange={handleTermsChange}
          loanType={LoanProductType.PAYABLE_FINANCING}
        />
      </div>
      <LoanCalculatorMessage message={message} />
    </div>
  );
};

export default PayablesFinanceLoanCalculator;
